type PathParam = string | number;

type StaticRoute = {
  absolute: string;
  relative?: string;
};

type DynamicRoute = StaticRoute & {
  build: (param: PathParam) => string;
};

const encodeParam = (value: PathParam) =>
  encodeURIComponent(String(value));

const staticRoute = (absolute: string, relative?: string): StaticRoute => ({
  absolute,
  ...(relative ? { relative } : {}),
});

const dynamicRoute = (
  absolute: string,
  placeholder: string,
  relative: string,
): DynamicRoute => ({
  absolute,
  relative,
  build: (param: PathParam) =>
    absolute.replace(`:${placeholder}`, encodeParam(param)),
});

export const paths = {
  public: {
    home: staticRoute("/"),
    apply: staticRoute("/apply"),
    programmes: staticRoute("/programmes"),
  },
  auth: {
    root: staticRoute("/auth"),
    login: staticRoute("/auth/login", "login"),
    register: staticRoute("/auth/register", "register"),
    forgotPassword: staticRoute("/auth/forgot-password", "forgot-password"),
    resetPassword: staticRoute("/auth/reset-password", "reset-password"),
    admissionStatus: staticRoute(
      "/auth/admission-status",
      "admission-status",
    ),
  },
  app: {
    root: staticRoute("/app"),
    dashboard: staticRoute("/app/dashboard", "dashboard"),
    applications: {
      root: staticRoute("/app/applications", "applications"),
      detail: dynamicRoute(
        "/app/applications/:applicationId",
        "applicationId",
        "applications/:applicationId",
      ),
    },
    programmes: {
      root: staticRoute("/app/programmes", "programmes"),
      detail: dynamicRoute(
        "/app/programmes/:programmeId",
        "programmeId",
        "programmes/:programmeId",
      ),
      create: staticRoute("/app/programmes/create", "programmes/create"),
      edit: dynamicRoute(
        "/app/programmes/:programmeId/edit",
        "programmeId",
        "programmes/:programmeId/edit",
      ),
      curriculum: dynamicRoute(
        "/app/programmes/:programmeId/curriculum",
        "programmeId",
        "programmes/:programmeId/curriculum",
      ),
      cohorts: dynamicRoute(
        "/app/programmes/:programmeId/cohorts",
        "programmeId",
        "programmes/:programmeId/cohorts",
      ),
    },
    faculties: {
      root: staticRoute("/app/faculties", "faculties"),
    },
    departments: {
      root: staticRoute("/app/departments", "departments"),
    },
    courses: {
      root: staticRoute("/app/courses", "courses"),
      learner: staticRoute("/app/learner-courses", "learner-courses"),
      feed: staticRoute("/app/course-feed", "course-feed"),
      create: staticRoute("/app/courses/create", "courses/create"),
      detail: dynamicRoute(
        "/app/courses/:courseId",
        "courseId",
        "courses/:courseId",
      ),
      content: dynamicRoute(
        "/app/courses/:courseId/content",
        "courseId",
        "courses/:courseId/content",
      ),
      manage: dynamicRoute(
        "/app/courses/:courseId/manage",
        "courseId",
        "courses/:courseId/manage",
      ),
      modules: {
        root: {
          absolute: "/app/courses/:courseId/modules",
          relative: "courses/:courseId/modules",
          build: (courseId: PathParam) =>
            `/app/courses/${encodeParam(courseId)}/modules`,
        },
        create: {
          absolute: "/app/courses/:courseId/modules/create",
          relative: "courses/:courseId/modules/create",
          build: (courseId: PathParam) =>
            `/app/courses/${encodeParam(courseId)}/modules/create`,
        },
        detail: {
          absolute: "/app/courses/:courseId/modules/:moduleId",
          relative: "courses/:courseId/modules/:moduleId",
          build: (courseId: PathParam, moduleId: PathParam) =>
            `/app/courses/${encodeParam(courseId)}/modules/${encodeParam(moduleId)}`,
        },
      },
    },
    assignments: {
      root: staticRoute("/app/assignments", "assignments"),
      detail: dynamicRoute(
        "/app/assignments/:assignmentId",
        "assignmentId",
        "assignments/:assignmentId",
      ),
      edit: dynamicRoute(
        "/app/assignments/:assignmentId/edit",
        "assignmentId",
        "assignments/:assignmentId/edit",
      ),
      submit: dynamicRoute(
        "/app/assignments/:assignmentId/submit",
        "assignmentId",
        "assignments/:assignmentId/submit",
      ),
      submissions: {
        list: {
          absolute: "/app/assignments/:assignmentId/submissions",
          relative: "assignments/:assignmentId/submissions",
          build: (assignmentId: PathParam) =>
            `/app/assignments/${encodeParam(assignmentId)}/submissions`,
        },
        detail: {
          absolute: "/app/assignments/:assignmentId/submissions/:submissionId",
          relative: "assignments/:assignmentId/submissions/:submissionId",
          build: (assignmentId: PathParam, submissionId: PathParam) =>
            `/app/assignments/${encodeParam(assignmentId)}/submissions/${encodeParam(submissionId)}`,
        },
      },
      create: staticRoute("/app/assignments/create", "assignments/create"),
    },
    payments: staticRoute("/app/payments", "payments"),
    quizzes: {
      root: staticRoute("/app/quizzes", "quizzes"),
      create: staticRoute("/app/quizzes/create", "quizzes/create"),
      edit: dynamicRoute(
        "/app/quizzes/:quizId/edit",
        "quizId",
        "quizzes/:quizId/edit",
      ),
      detail: dynamicRoute(
        "/app/quizzes/:quizId",
        "quizId",
        "quizzes/:quizId",
      ),
      attempts: {
        list: dynamicRoute(
          "/app/quizzes/:quizId/attempts",
          "quizId",
          "quizzes/:quizId/attempts",
        ),
        detail: {
          absolute: "/app/quizzes/:quizId/attempts/:attemptId",
          relative: "quizzes/:quizId/attempts/:attemptId",
          build: (quizId: PathParam, attemptId: PathParam) =>
            `/app/quizzes/${encodeParam(quizId)}/attempts/${encodeParam(attemptId)}`,
        },
      },
      courseList: {
        absolute: "/app/courses/:courseId/quizzes",
        relative: "courses/:courseId/quizzes",
        build: (courseId: PathParam) =>
          `/app/courses/${encodeParam(courseId)}/quizzes`,
      },
      take: {
        absolute: "/app/courses/:courseId/quizzes/:quizId/attempts/:attemptId/take",
        relative: "courses/:courseId/quizzes/:quizId/attempts/:attemptId/take",
        build: (courseId: PathParam, quizId: PathParam, attemptId: PathParam) =>
          `/app/courses/${encodeParam(courseId)}/quizzes/${encodeParam(quizId)}/attempts/${encodeParam(attemptId)}/take`,
      },
      learnerAttemptDetail: {
        absolute: "/app/courses/:courseId/quizzes/:quizId/attempts/:attemptId",
        relative: "courses/:courseId/quizzes/:quizId/attempts/:attemptId",
        build: (courseId: PathParam, quizId: PathParam, attemptId: PathParam) =>
          `/app/courses/${encodeParam(courseId)}/quizzes/${encodeParam(quizId)}/attempts/${encodeParam(attemptId)}`,
      },
    },
    certificates: staticRoute("/app/certificates", "certificates"),
    transcript: staticRoute("/app/transcript", "transcript"),
    announcements: {
      root: staticRoute("/app/announcements", "announcements"),
      detail: dynamicRoute(
        "/app/announcements/:announcementId",
        "announcementId",
        "announcements/:announcementId",
      ),
    },
    discussions: {
      root: staticRoute("/app/discussions", "discussions"),
      detail: dynamicRoute(
        "/app/discussions/:discussionId",
        "discussionId",
        "discussions/:discussionId",
      ),
    },
    analytics: staticRoute("/app/analytics", "analytics"),
    roleManagement: staticRoute("/app/role-management", "role-management"),
    studentManagement: {
      root: staticRoute("/app/student-management", "student-management"),
      edit: dynamicRoute(
        "/app/student-management/:userId/edit",
        "userId",
        "student-management/:userId/edit",
      ),
    },
    users: {
      root: staticRoute("/app/users", "users"),
      detail: dynamicRoute(
        "/app/users/:userId",
        "userId",
        "users/:userId",
      ),
      edit: dynamicRoute(
        "/app/users/:userId/edit",
        "userId",
        "users/:userId/edit",
      ),
      licenses: dynamicRoute(
        "/app/users/:userId/licenses",
        "userId",
        "users/:userId/licenses",
      ),
    },
    export: staticRoute("/app/export", "export"),
    settings: staticRoute("/app/settings", "settings"),
    notificationSettings: staticRoute(
      "/app/notification-settings",
      "notification-settings",
    ),
    uploadResources: staticRoute("/app/upload-resources", "upload-resources"),
    profile: staticRoute("/app/profile", "profile"),
    profileEdit: staticRoute("/app/profile/edit", "profile/edit"),
    more: staticRoute("/app/more", "more"),
    profileSettings: staticRoute("/app/profile-settings", "profile-settings"),
    systemSettings: staticRoute("/app/system-settings", "system-settings"),
    securitySettings: staticRoute("/app/security-settings", "security-settings"),
    help: staticRoute("/app/help", "help"),
    systemStatus: staticRoute("/app/system-status", "system-status"),
  },
  catchAll: "*",
} as const;

export type AppPaths = typeof paths;
